import { ButtonAlert } from 'components/ButtonAlert';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { getColumnId } from 'components/TableSettings';
import { TableTopPanel } from 'components/TableTopPanel';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimActKc2Data } from 'gql/graphql';
import { CostGroupEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';

import type { ColDef } from 'ag-grid-community';
import type React from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useBimRemoveActsKc2 } from '../hooks';

interface IRemoveActsKc2Panel {
    checkedIds: number[];
    setCheckedIds: React.Dispatch<React.SetStateAction<number[]>>;
    workOrderPriceId: number;
    refreshData: () => void;
    columns: ColDef<BimActKc2Data>[];
    columnsSelection: Record<string, boolean>;
    onColumnsSelectionChange: (selection: Record<string, boolean>) => void;
}

export const ActsKc2TopPanel: React.FC<IRemoveActsKc2Panel> = (props) => {
    const {
        checkedIds,
        setCheckedIds,
        workOrderPriceId,
        refreshData,
        columns,
        onColumnsSelectionChange,
        columnsSelection,
    } = props;

    const { id: projectId, orderId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const { aktKC2Bim: aktKC2BimRules } = useAccess();

    const resetCheckedRows = useCallback(() => {
        setCheckedIds([]);
    }, [setCheckedIds]);

    const onRemoveSuccess = useCallback(() => {
        resetCheckedRows();
        refreshData();
    }, [refreshData, resetCheckedRows]);

    const { bimRemoveActsKc2MakeRequest, bimRemoveActsKc2Status } = useBimRemoveActsKc2(
        workOrderPriceId,
        onRemoveSuccess,
    );

    const onRemoveClick = useCallback(() => {
        sendGpnAnalyticsEvent({
            componentId: 'rim_act KC delete button_pressed',
            projectId,
            orderId: Number(orderId),
            costGroup: CostGroupEnum.Cmr,
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectDO: projectData?.subsidiary?.name,
            projectType: projectData?.contractType,
        });

        bimRemoveActsKc2MakeRequest(checkedIds);
    }, [
        bimRemoveActsKc2MakeRequest,
        checkedIds,
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onRemoveClick,
        });

    const onTableSettingsConfirm = useCallback(
        (visible) => {
            const hidden = columns.reduce(
                (acc, column: any) => {
                    acc[getColumnId(column)] = false;

                    return acc;
                },
                {} as Record<string, false>,
            );

            onColumnsSelectionChange({ ...hidden, ...visible });
        },
        [columns, onColumnsSelectionChange],
    );

    const isColumnVisible = useCallback((id: string) => columnsSelection[id] ?? true, [columnsSelection]);

    return (
        <TableTopPanel
            style={{ margin: '5px 0' }}
            checkedRowsArr={checkedIds}
            resetCheckedRows={resetCheckedRows}
            optionalColumns={columns}
            handleTableSettingsConfirm={onTableSettingsConfirm}
            isColumnVisible={isColumnVisible}
            areChildrenOnLeftSide
            showCheckedOnly
        >
            <ButtonAlert
                label="Удалить"
                size="xs"
                onClick={handleOpenConfirmationDialog}
                disabled={bimRemoveActsKc2Status === RequestStatus.FETCHING || !aktKC2BimRules.canDelete}
                style={{ marginRight: 10 }}
            />
            <ConfirmationDialog
                textHeader="Удаление Акта КС-2"
                textConfirmation="Вы уверены, что хотите удалить Акт КС-2?"
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </TableTopPanel>
    );
};

ActsKc2TopPanel.displayName = 'RemoveActsKc2Panel';
